import { useRef, useState } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import { useSpaceScope } from '@/contexts/SpaceScopeContext';
import { ApiError, spacesApi } from '@/lib/api';
import { updateRuleEnabled } from '@/lib/spaces/rules';
import { track } from '@/lib/telemetry';
import type { MySpaceRulesOverview, SpaceSendRule } from '@/types/spaces';
import { LONG_LIVED_SWR_OPTIONS, useAuthedFetcher } from './swrConfig';
import { useSpaceWrite } from './useSpaceWrite';

export function useSpaceRulesOverview() {
  const { enabled, scope } = useSpaceScope();
  const { userId, write, refresh, mutate } = useSpaceWrite();
  const fetch = useAuthedFetcher();
  const active = enabled && scope === 'personal' && Boolean(userId);
  const query = useSWR<MySpaceRulesOverview, Error>(active ? ['space-rules-overview', userId] : null,
    () => fetch(spacesApi.rulesOverview), { ...LONG_LIVED_SWR_OPTIONS, keepPreviousData: false, shouldRetryOnError: false, refreshInterval: 60_000 });
  const [changingRuleId, setChangingRuleId] = useState<string | null>(null);
  const changing = useRef(false);

  const toggleRule = async (rule: SpaceSendRule) => {
    if (!active || !query.data || query.error || changing.current) return;
    const snapshot = query.data;
    changing.current = true;
    setChangingRuleId(rule.id);
    try {
      await query.mutate(async () => {
        await write((token) => spacesApi.setRuleEnabled(token, rule.id, !rule.enabled));
        return undefined;
      }, {
        optimisticData: (data) => updateRuleEnabled(data ?? snapshot, rule.id, !rule.enabled),
        rollbackOnError: true, populateCache: false, revalidate: true,
      });
      if (rule.enabled) track('space.rule.paused', { rule_type: rule.type });
      toast.success(rule.enabled ? 'Regra pausada. As despesas já enviadas continuam no espaço.' : 'Regra retomada. Os próximos envios seguem as regras do espaço.');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Não foi possível alterar a regra. Tente novamente.');
    } finally {
      // A lost write response may still have changed the server; refresh every consumer.
      refresh(['space-send-rules', userId]);
      void mutate((key) => Array.isArray(key) && key[0] === 'space-send-preview' && key[1] === userId).catch(() => undefined);
      changing.current = false;
      setChangingRuleId(null);
    }
  };

  return { ...query, data: active && !query.error ? query.data : undefined, changingRuleId, toggleRule };
}
